import React, { useState, useEffect } from 'react';
import TheaterLoader from '../components/TheaterLoader';
import HeroCarousel from '../components/HeroCarousel';
import Ticker from '../components/Ticker';
import UpcomingShows from '../components/UpcomingShows';
import TodayShows from '../components/TodayShows';
import Categories from '../components/Categories';
import PartyCamps from '../components/PartyCamps';
import ScheduledShows from '../components/ScheduledShows';
import RecommendedShows from '../components/RecommendedShows';
import TopActors from '../components/TopActors';
import CommunityBanner from '../components/CommunityBanner';
import ValueProps from '../components/ValueProps';
import ListYourShow from '../components/ListYourShow';
import axios from 'axios';

import LiveBookingShows from '../components/LiveBookingShows';

// Helper to convert backend array/object/empty response safely into an array
const toSafeArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val && typeof val === 'object') return Object.values(val);
  return [];
};

export default function Home() {
  const [homepageData, setHomepageData] = useState(null);
  const [actorsData, setActorsData] = useState([]);
  const [hasScheduledShows, setHasScheduledShows] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Client-ID': 'lokanatya_client',
        'Client-Secret': 'super_secret_key_123'
      };

      const dist_id = localStorage.getItem('selected_dist_id');
      const distQuery = dist_id ? `?dist_id=${dist_id}` : '';

      const [homeRes, actorsRes] = await Promise.all([
        axios.get(`/api/fetch_homepage_data${distQuery}`, { headers }).catch(err => {
          console.error('Home data error:', err);
          return null;
        }),
        axios.get('/api/fetch_all_actor', { headers }).catch(err => {
          console.error('Actors data error:', err);
          return null;
        })
      ]);

      if (homeRes && homeRes.data) {
        setHomepageData(homeRes.data);
      }
      if (actorsRes && actorsRes.data && actorsRes.data.status) {
        setActorsData(toSafeArray(actorsRes.data.data));
      }
    } catch (error) {
      console.error('Error fetching homepage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    window.addEventListener('locationChanged', fetchData);
    return () => window.removeEventListener('locationChanged', fetchData);
  }, []);

  const categoryList = toSafeArray(homepageData?.category);
  const bannerList = toSafeArray(homepageData?.banner);
  const newBannerList = toSafeArray(homepageData?.new_banner);
  const todayShowsList = toSafeArray(homepageData?.today_show);
  const liveBookingList = toSafeArray(homepageData?.live_booking);
  const upcomingShowsList = toSafeArray(homepageData?.product_upcoming);
  const recommendedShowsList = toSafeArray(homepageData?.product_recommeded);
  const campsList = toSafeArray(homepageData?.camp_details);

  return (
    <>
      <TheaterLoader />
      {loading ? (
        <div className="bg-[#fcfcfc] pb-8 animate-pulse">
          {/* Hero Skeleton */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
            <div className="w-full h-[220px] md:h-[350px] bg-gray-200 rounded-3xl"></div>
          </div>

          {/* Section Skeleton (mimics Today/Upcoming Shows) */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
            <div className="h-6 bg-gray-200 w-48 mb-6 rounded"></div>
            <div className="flex gap-4 overflow-hidden">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="min-w-[280px] md:min-w-[320px] h-[180px] bg-gray-200 rounded-2xl shrink-0"></div>
              ))}
            </div>
          </div>

          {/* Section Skeleton 2 */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <div className="h-6 bg-gray-200 w-48 mb-6 rounded"></div>
            <div className="flex gap-4 overflow-hidden">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="min-w-[280px] md:min-w-[320px] h-[180px] bg-gray-200 rounded-2xl shrink-0"></div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#fcfcfc] pb-3">
          <Categories categories={categoryList} />
          <HeroCarousel banners={bannerList} newBanners={newBannerList} liveBookingShows={liveBookingList.length > 0 ? liveBookingList : todayShowsList} />
          <Ticker todayShows={todayShowsList} />

          <TodayShows shows={todayShowsList} />
          <LiveBookingShows shows={liveBookingList.length > 0 ? liveBookingList : todayShowsList} />
          <UpcomingShows shows={upcomingShowsList} />

          <PartyCamps camps={campsList} categories={categoryList} />

          {/* Split / Full-width section for Scheduled & Recommended Shows */}
          {(hasScheduledShows || (recommendedShowsList && recommendedShowsList.length > 0)) && (
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 mt-8 mb-8">
              {hasScheduledShows ? (
                <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                  <div className="w-full lg:w-1/2">
                    <ScheduledShows onDataLoaded={setHasScheduledShows} />
                  </div>
                  <div className="w-full lg:w-1/2">
                    <RecommendedShows shows={recommendedShowsList} isFullWidth={false} />
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <ScheduledShows onDataLoaded={setHasScheduledShows} />
                  <RecommendedShows shows={recommendedShowsList} isFullWidth={true} />
                </div>
              )}
            </div>
          )}

          <TopActors actors={actorsData} />
          <CommunityBanner />
          <ValueProps />
          <ListYourShow />
        </div>
      )}
    </>
  );
}
